import { useNavigate } from "react-router-dom";
import { useCampaign } from "@/contexts/CampaignContext";
import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Pencil, Send } from "lucide-react";
import { format } from "date-fns";
import { submitCampaign } from "@/api/campaign";
import { useToast } from "@/hooks/useToast";
import { useState } from "react";

export function CampaignOverview() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    campaignDetails,
    audienceTargeting,
    creativeFiles,
    contactInfo
  } = useCampaign();

  console.log('Campaign Overview Data:', {
    campaignDetails,
    audienceTargeting,
    creativeFiles,
    contactInfo
  });

  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      console.log('Starting campaign submission...');
      setIsSubmitting(true);
      const response = await submitCampaign({
        campaignDetails,
        audienceTargeting,
        creativeFiles,
        contactInfo,
      });
      console.log('Campaign submission response:', response);

      if (response.success) {
        // Navigate first, clear state in SubmittedPage
        navigate("/submitted");
      } else {
        throw new Error(response.message || 'Failed to submit campaign');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <CardHeader>
        <CardTitle>Campaign Overview</CardTitle>
        <CardDescription>Review your campaign details before submission</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Campaign Details</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/create")}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Client Name</p>
              <p className="font-medium">{campaignDetails?.clientName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Campaign Name</p>
              <p className="font-medium">{campaignDetails?.campaignName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Budget</p>
              <p className="font-medium">${campaignDetails?.budget.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">CPM</p>
              <p className="font-medium">${campaignDetails?.cpm}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Duration</p>
              <p className="font-medium">
                {format(new Date(campaignDetails?.startDate), "MMM d, yyyy")} -{" "}
                {format(new Date(campaignDetails?.endDate), "MMM d, yyyy")}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Day Parting</p>
              <p className="font-medium">{campaignDetails?.dayParting}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Impressions</p>
              <p className="font-medium">{campaignDetails?.impressions?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Floor Frequency</p>
              <p className="font-medium">{campaignDetails?.floorFrequency}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Audience Reach</p>
              <p className="font-medium">{campaignDetails?.audienceReach?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Content Focus</p>
              <p className="font-medium">{campaignDetails?.contentFocus.join(", ")}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Notes</p>
              <p className="font-medium">{campaignDetails?.notes || "N/A"}</p>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Audience Targeting</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/create/audience")}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">ZIP Code & Radius</p>
              <p className="font-medium">
                {audienceTargeting?.zipCode} ({audienceTargeting?.radius} miles)
              </p>
              <p className="text-sm text-muted-foreground mt-4">Included ZIP Codes</p>
              <p className="font-medium">{audienceTargeting?.zipCodes.join(", ")}</p>
              <p className="text-sm text-muted-foreground mt-4">Selected Segments</p>
              <p className="font-medium">{audienceTargeting?.segmentLabels?.join(", ") || "None selected"}</p>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">Audience Metrics</p>
                <div className="mt-2 space-y-1">
                  <p>Total Audience: {audienceTargeting?.audienceMetrics?.totalAudience?.toLocaleString()}</p>
                  <p>Emails: {audienceTargeting?.audienceMetrics?.totalEmails?.toLocaleString()}</p>
                  <p>Phones: {audienceTargeting?.audienceMetrics?.totalPhones?.toLocaleString()}</p>
                  <p>Devices: {audienceTargeting?.audienceMetrics?.totalDevices?.toLocaleString()}</p>
                  <p>IPs: {audienceTargeting?.audienceMetrics?.totalIPs?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Creative Files</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/create/creative")}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              {creativeFiles?.files.map((file, index) => (
                <div key={index} className="space-y-2">
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                    <video
                      src={file.url}
                      className="w-full h-full object-contain"
                      controls
                    />
                  </div>
                  <p className="font-medium truncate">{file.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/create/contact")}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Agency Name</p>
              <p className="font-medium">{contactInfo?.agencyName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact Name</p>
              <p className="font-medium">{contactInfo?.contactName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact Email</p>
              <p className="font-medium">{contactInfo?.contactEmail}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact Phone</p>
              <p className="font-medium">{contactInfo?.contactPhone}</p>
            </div>
          </div>
        </section>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => navigate("/create/contact")}>
          Back
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          <Send className="mr-2 h-4 w-4" />
          {isSubmitting ? "Submitting..." : "Submit Campaign"}
        </Button>
      </CardFooter>
    </PageContainer>
  );
}